import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Calendar, Clock, MapPin, Users, DollarSign, Eye, Download, FileText, Loader2, MessageSquare } from 'lucide-react';
import ClientPageLayout from '../../components/shared/layouts/ClientPageLayout';
import { useClientBookings } from '../../services/bookingService';
import { clientInvoiceApi, useClientPendingInvoices } from '../../services/clientInvoiceService';
import { useToastContext } from '../../contexts/ToastContext';
import { SearchSelect } from '../../components/shared';
import { DatePicker } from '../../components/client';
import { getStatusColor, formatStatusText, formatDate, formatTime } from '../../utils';
import { downloadInvoicePdfFromInvoiceData } from '../../utils/vendorInvoicePdf';

const ClientBookings = () => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToastContext();
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    start_date: '',
    end_date: ''
  });

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'no_show', label: 'No Show' }
  ];

  // Clean filters to remove empty values that cause database errors
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
  );

  const { data: bookingsResponse, isLoading, error } = useClientBookings(cleanFilters);
  const { data: pendingInvoicesRes } = useClientPendingInvoices();
  const pendingInvoiceCount = pendingInvoicesRes?.data?.count ?? 0;
  
  // Extract bookings array from response, with fallback to empty array
  // The API returns: { success: true, data: { data: [...], pagination: ... } }
  const bookingsRaw = bookingsResponse?.data?.data || bookingsResponse?.data || bookingsResponse || [];
  
  // Sort bookings by created_at (newest first), then by event_date as fallback
  const bookings = Array.isArray(bookingsRaw) ? [...bookingsRaw].sort((a, b) => {
    const dateA = new Date(a.created_at || a.event_date || 0);
    const dateB = new Date(b.created_at || b.event_date || 0);
    return dateB - dateA; // Descending order (newest first)
  }) : [];



  // Helper function to safely parse and format currency
  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num) || num === null || num === undefined) {
      return '0.00';
    }
    return num.toFixed(2);
  };

  // Helper function to calculate total due
  const calculateTotalDue = (booking) => {
    const subtotal = parseFloat(booking?.subtotal_amount) || 0;
    const platformFee = parseFloat(booking?.platform_fee) || 0;
    return subtotal + platformFee;
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      start_date: '',
      end_date: ''
    });
  };

  const handleDownloadInvoice = async (invoiceId, invoiceNumber) => {
    setDownloadingInvoiceId(invoiceId);
    try {
      const res = await clientInvoiceApi.getOne(invoiceId);
      const inv = res?.data;
      if (!inv) {
        showError('Could not load invoice.');
        return;
      }
      const vendorProfile = inv.vendor || {};
      await downloadInvoicePdfFromInvoiceData(inv, vendorProfile);
      await clientInvoiceApi.markDownloaded(invoiceId);
      await queryClient.invalidateQueries({ queryKey: ['client-invoices-pending'] });
      await queryClient.invalidateQueries({ queryKey: ['bookings'] });
      success(`Invoice ${invoiceNumber || ''} downloaded.`.trim());
    } catch {
      showError('Could not download invoice. Please try again.');
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  // Skeleton Loading Component
  const BookingSkeleton = () => (
    <div className="p-6 animate-pulse">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-3">
            <div className="h-5 w-32 bg-gray-200 rounded"></div>
            <div className="h-6 w-20 bg-gray-200 rounded-full"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center space-x-2">
                <div className="h-4 w-4 bg-gray-200 rounded"></div>
                <div className="h-4 w-24 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
          <div className="mb-4">
            <div className="h-5 w-48 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 w-32 bg-gray-200 rounded mb-1"></div>
            <div className="h-4 w-40 bg-gray-200 rounded"></div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <div className="h-6 w-24 bg-gray-200 rounded"></div>
              <div className="h-4 w-32 bg-gray-200 rounded"></div>
            </div>
            <div className="h-10 w-full sm:w-32 bg-gray-200 rounded-md"></div>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <ClientPageLayout title="My Bookings">
        <div className="space-y-6">
          {/* Filters Skeleton */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
            <div className="h-6 w-32 bg-gray-200 rounded mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-10 w-full bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>

          {/* Bookings List Skeleton */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="h-6 w-40 bg-gray-200 rounded"></div>
            </div>
            <div className="divide-y divide-gray-200">
              {[1, 2, 3].map((i) => (
                <BookingSkeleton key={i} />
              ))}
            </div>
          </div>
        </div>
      </ClientPageLayout>
    );
  }

  if (error) {
    return (
      <ClientPageLayout title="My Bookings">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="text-center">
            <div className="text-red-600 mb-4">Error loading bookings</div>
            <p className="text-gray-600">{error.message}</p>
          </div>
        </div>
      </ClientPageLayout>
    );
  }

  return (
    <ClientPageLayout title="My Bookings">
      <div className="space-y-6">
        {pendingInvoiceCount > 0 && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3">
            <FileText className="w-5 h-5 text-amber-900 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-950">
              You have {pendingInvoiceCount} pending invoice
              {pendingInvoiceCount > 1 ? 's' : ''}. Use <strong>Download invoice</strong> on the
              booking below to save a PDF copy.
            </p>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 md:p-6">
          <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3">Filter Bookings</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Status</label>
              <SearchSelect
                options={statusOptions}
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
                placeholder="Select Status"
                className="w-full text-xs sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">From Date</label>
              <DatePicker
                value={filters.start_date}
                onChange={(value) => handleFilterChange('start_date', value)}
                placeholder="Select start date"
                className="w-full text-xs sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">To Date</label>
              <DatePicker
                value={filters.end_date}
                onChange={(value) => handleFilterChange('end_date', value)}
                placeholder="Select end date"
                className="w-full text-xs sm:text-sm"
              />
            </div>
            <div className="flex items-end sm:col-span-2 lg:col-span-1">
              <button
                onClick={clearFilters}
                className="w-full px-3 py-1.5 sm:px-4 sm:py-2 bg-gray-100 text-gray-700 text-xs sm:text-sm rounded-md hover:bg-gray-200 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Bookings List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Your Bookings ({bookings?.length || 0})
            </h3>
          </div>

          {!Array.isArray(bookings) || bookings.length === 0 ? (
            <div className="p-8 text-center">
              <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No bookings found</h3>
              <p className="text-gray-600 mb-4">
                {Object.values(filters).some(f => f) 
                  ? "No bookings match your current filters." 
                  : "You haven't made any bookings yet."
                }
              </p>
              <Link
                to="/browse-vendors"
                className="inline-flex items-center px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
              >
                Browse Vendors
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {Array.isArray(bookings) && bookings.map((booking) => (
                <div
                  key={booking.id}
                  className="p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 sm:gap-3 mb-3">
                        <h4 className="text-md font-semibold text-gray-900">
                          #{booking.booking_reference}
                        </h4>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-light ${getStatusColor(booking.status)}`}>
                          {formatStatusText(booking.status)}
                        </span>
                        {booking.client_invoice?.is_pending && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900">
                            Invoice pending
                          </span>
                        )}
                        {(Number(booking.unread_chat_count) || 0) > 0 && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-600 text-white">
                            <MessageSquare className="h-3 w-3" />
                            {Number(booking.unread_chat_count) > 99 ? '99+' : Number(booking.unread_chat_count)}{' '}
                            new {Number(booking.unread_chat_count) === 1 ? 'message' : 'messages'}
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {formatDate(booking.event_date)}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Clock className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {booking.event_location || 'Location TBD'}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Users className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {booking.guest_count || 0} guests
                          </span>
                        </div>
                      </div>

                      <div className="mb-4">
                        <h5 className="font-medium text-gray-900 mb-2">
                          {booking.vendor_service?.service_name || 'Service Name'}
                        </h5>
                        <p className="text-sm text-gray-600">
                          {booking.vendor_service?.service_category?.category_name || 'Category'}
                        </p>
                        {booking.vendor_service_package && (
                          <p className="text-sm text-gray-500 mt-1">
                            Package: {booking.vendor_service_package.package_name}
                          </p>
                        )}
                      </div>

                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                          {(parseFloat(booking?.discount_amount) || 0) > 0 && (
                            <div className="text-sm text-green-600 font-medium">
                              Discount{booking?.discount_code?.code ? ` (${booking.discount_code.code})` : ''}: -${formatCurrency(booking.discount_amount)}
                            </div>
                          )}
                          <div className="flex items-center space-x-2">
                            <DollarSign className="h-4 w-4 text-gray-400" />
                            <span className="text-lg font-semibold text-gray-900">
                              ${formatCurrency(calculateTotalDue(booking))}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            Paid: ${formatCurrency(booking?.amount_paid)}
                          </div>
                          {(() => {
                            const totalDue = calculateTotalDue(booking);
                            const amountPaid = parseFloat(booking?.amount_paid) || 0;
                            const remaining = Math.max(0, totalDue - amountPaid);
                            const isFullyPaid = totalDue > 0 && amountPaid >= totalDue;
                            
                            return isFullyPaid ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Fully Paid
                              </span>
                            ) : remaining > 0 ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                Balance: ${formatCurrency(remaining)}
                              </span>
                            ) : null;
                          })()}
                        </div>

                        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                          {booking.client_invoice && (
                            <button
                              type="button"
                              onClick={() =>
                                handleDownloadInvoice(
                                  booking.client_invoice.id,
                                  booking.client_invoice.invoice_number
                                )
                              }
                              disabled={downloadingInvoiceId === booking.client_invoice.id}
                              className="inline-flex items-center justify-center px-3 py-1.5 border border-gray-900 text-gray-900 text-xs font-medium rounded-full w-full sm:w-auto hover:bg-gray-100 disabled:opacity-60"
                            >
                              {downloadingInvoiceId === booking.client_invoice.id ? (
                                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                              ) : (
                                <Download className="h-3.5 w-3.5 mr-1.5" />
                              )}
                              Download invoice
                            </button>
                          )}
                          <Link
                            to={`/bookings/${booking.id}`}
                            className="relative inline-flex items-center justify-center px-3 py-1.5 bg-black text-white text-xs rounded-full w-full sm:w-auto hover:bg-gray-800"
                          >
                            <Eye className="h-3.5 w-3.5 mr-1.5" />
                            View Details
                            {(Number(booking.unread_chat_count) || 0) > 0 && (
                              <span className="absolute -top-1.5 -right-1.5 min-w-[1.25rem] h-5 px-1 flex items-center justify-center rounded-full bg-red-600 text-white text-[11px] font-semibold leading-none">
                                {Number(booking.unread_chat_count) > 99 ? '99+' : Number(booking.unread_chat_count)}
                              </span>
                            )}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </ClientPageLayout>
  );
};

export default ClientBookings;